<script setup lang="ts">
import { countries } from '~/assets/data/countries';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from 'zod';
import Logo from '@images/logos/eaccme.svg';
import { CourseCertificateStatus } from "~/enums/course.enums";

import {
  validationOptions,
  options,

  UsefulnessOptions,
  UsefulnessOptionsValuesMapping,

  OverallImpressionOptions,
  OverallImpressionOptionsValuesMapping,

  EducationalGoalsAndScientificEvidenceOptions,
  EducationalGoalsAndScientificEvidenceOptionsValuesMapping,

  LearnerEngagementTimeOptions,
  LearnerEngagementTimeOptionsValuesMapping,

  InformationImplementationOptions,
  InformationImplementationOptionsValuesMapping,

  InformationFreeOfBiasOptions,
  InformationFreeOfBiasOptionsValuesMapping,

  FacultyConflictDeclarationOptions,
  FacultyConflictDeclarationOptionsValuesMapping,

} from "~/enums/certificates/eaccme.enums";

const { dispatchMessage } = useSystemMessages();
const { submitClaimCertificateForm, startDownload } = useCertificate();
const { course } = useCourseState();
const { user } = useUserStore();

const loading = ref(false);

const emit = defineEmits<{
  (e: 'downloaded'): void;
}>();

/*
 * schema
 */
const schema = toTypedSchema(
  zod.object({

    /**
     * PERSONAL INFORMATION
     */
    firstName: zod.string().min(1, 'This field is required'),
    lastName: zod.string().min(1, 'This field is required'),

    userIsDoctorOfMedicine: zod.boolean({ message: 'This field is required' }),

    country: zod.object({
      isoCode: zod.string({ message: 'This field is required' })
        .refine(countryIsoCode => countries.map(country => country.code).includes(countryIsoCode), {
          message: 'This field is required',
        }),
      stateName: zod.string().optional(),
    }).refine(country => {
      if (country.isoCode === 'US' && (!country.stateName || country.stateName.trim() === '')) {
        return false;
      }
      return true;
    }, {
      message: "State name is required for US",
      path: ['stateName'],
    }),

    /**
     * QUALITY OF ELM
     */
    usefulnessOfMaterial: zod.object({
      // has to be in UsefulnessOptions
      usefulnessProfessional: zod.string({ message: 'This field is required' })
        .refine(
          val => Object.values(UsefulnessOptions).includes(val),
          { message: 'This field is required' },
        ),
      notUsefulExplanationProfessional: zod.string().optional(),

      usefulnessPersonal: zod.string({ message: 'This field is required' })
        .refine(
          val => Object.values(UsefulnessOptions).includes(val),
          { message: 'This field is required' },
        ),
      notUsefulExplanationPersonal: zod.string().optional(),
    }).refine(data => {
      return !(
        data.usefulnessProfessional === UsefulnessOptions[1] &&
        (!data.notUsefulExplanationProfessional || data.notUsefulExplanationProfessional.trim() === '')
      );

    },
    {
      message: "This field is required if material was not useful",
      path: ['notUsefulExplanationProfessional'],

    }).refine(data => {
      return !(
        data.usefulnessPersonal === UsefulnessOptions[1] &&
        (!data.notUsefulExplanationPersonal || data.notUsefulExplanationPersonal.trim() === '')
      );
    },
    {
      message: "This field is required if was not useful",
      path: ['notUsefulExplanationPersonal'],
    }),

    overallImpressionProgramme: zod.string({ message: 'This field is required' })
      .refine(
        val => Object.values(OverallImpressionOptions).includes(val),
        { message: 'This field is required' },
      ),

    overallImpressionOrganization: zod.string({ message: 'This field is required' })
      .refine(
        val => Object.values(OverallImpressionOptions).includes(val),
        { message: 'This field is required' },
      ),

    bestAspect: zod.string().min(1, 'This field is required'),
    worstAspect: zod.string().min(1, 'This field is required'),

    /**
     * RELEVANCE OF THE MATERIAL
     */
    achievementOfEducationalGoals: zod.string({ message: 'This field is required' })
      .refine(
        val => Object.values(EducationalGoalsAndScientificEvidenceOptions).includes(val),
        { message: 'This field is required' },
      ),
    basedOnScientificEvidence: zod.string({ message: 'This field is required' })
      .refine(
        val => Object.values(EducationalGoalsAndScientificEvidenceOptions).includes(val),
        { message: 'This field is required' },
      ),

    /**
     * SUITABILITY OF THE FORMATS USED DURING THE MATERIAL
     */
    learnerEngagementTime: zod.string({ message: 'This field is required' })
      .refine(
        val => Object.values(LearnerEngagementTimeOptions).includes(val),
        { message: 'This field is required' },
      ),

    innovativeElements: zod.string().min(1, 'This field is required'),

    /**
     * WAYS THE MATERIAL AFFECTS CLINICAL PRACTICE
     */
    informationImplementation: zod.string({ message: 'This field is required' })
      .refine(
        val => Object.values(InformationImplementationOptions).includes(val),
        { message: 'This field is required' },
      ),

    futurePracticeInfluence: zod.string().min(1, 'This field is required'),

    /**
     * COMMERCIAL BIAS
     */
    facultyConflictDeclaration: zod.string({ message: 'This field is required' })
      .refine(
        val => Object.values(FacultyConflictDeclarationOptions).includes(val),
        { message: 'This field is required' },
      ),

    biasedPresentationExample: zod.string().min(1, 'This field is required'),

    informationFreeOfBias: zod.string({ message: 'This field is required' })
      .refine(
        val => Object.values(InformationFreeOfBiasOptions).includes(val),
        { message: 'This field is required' },
      ),

    /**
     * GENERAL
     */
    userAgreedToDataSharing: zod.boolean({ message: 'This field is required' })
      .refine(data => data,
        { message: "This field is required" },
      ),

    courseRating: zod.number({ message: 'This field is required' })
      .min(1, 'Rating must be at least 1')
      .max(5, 'Rating must be at most 5'),
  }),
);

const { defineField, errors, handleSubmit } = useForm({
  validationSchema: schema,
  initialValues: {
    firstName: user.value?.firstName || '',
    lastName: user.value?.lastName || '',

    country: {
      isoCode: '',
    },

    /**
     * QUALITY OF ELM
     */
    usefulnessOfMaterial: {
      usefulnessProfessional: '',
      notUsefulExplanationProfessional: '',

      usefulnessPersonal: '',
      notUsefulExplanationPersonal: '',
    },
    overallImpressionProgramme: '',
    overallImpressionOrganization: '',
    bestAspect: '',
    worstAspect: '',

    /**
     * RELEVANCE OF THE MATERIAL
     */
    achievementOfEducationalGoals: '',
    basedOnScientificEvidence: '',

    /**
     * SUITABILITY OF THE FORMATS USED DURING THE MATERIAL
     */
    learnerEngagementTime: '',
    innovativeElements: '',

    /**
     * WAYS THE MATERIAL AFFECTS CLINICAL PRACTICE
     */
    informationImplementation: '',
    futurePracticeInfluence: '',

    /**
     * COMMERCIAL BIAS
     */
    facultyConflictDeclaration: '',
    biasedPresentationExample: '',
    informationFreeOfBias: '',

    /**
     * GENERAL
     */
    userAgreedToDataSharing: false,
  },
});

/**
 * PERSONAL INFORMATION
 */
const [firstName, firstNameProps] = defineField("firstName", validationOptions);
const [lastName, lastNameProps] = defineField("lastName", validationOptions);

const [userIsDoctorOfMedicine, userIsDoctorOfMedicineProps] = defineField("userIsDoctorOfMedicine", validationOptions);

const [countryIsoCode, countryIsoCodeProps] = defineField("country.isoCode", validationOptions);
const [countryStateName, countryStateNameProps] = defineField("country.stateName", validationOptions);

/**
 * QUALITY OF ELM
 */
const [usefulnessProfessional, usefulnessProfessionalProps] = defineField("usefulnessOfMaterial.usefulnessProfessional", validationOptions);
const [notUsefulExplanationProfessional, notUsefulExplanationProfessionalProps] = defineField("usefulnessOfMaterial.notUsefulExplanationProfessional", validationOptions);

const [overallImpressionProgramme, overallImpressionProgrammeProps] = defineField("overallImpressionProgramme", validationOptions);
const [overallImpressionOrganization, overallImpressionOrganizationProps] = defineField("overallImpressionOrganization", validationOptions);

const [bestAspect, bestAspectProps] = defineField("bestAspect", validationOptions);
const [worstAspect, worstAspectProps] = defineField("worstAspect", validationOptions);

/**
 * RELEVANCE OF THE MATERIAL
 */
const [achievementOfEducationalGoals, achievementOfEducationalGoalsProps] = defineField("achievementOfEducationalGoals", validationOptions);
const [basedOnScientificEvidence, basedOnScientificEvidenceProps] = defineField("basedOnScientificEvidence", validationOptions);

const [usefulnessPersonal, usefulnessPersonalProps] = defineField("usefulnessOfMaterial.usefulnessPersonal", validationOptions);
const [notUsefulExplanationPersonal, notUsefulExplanationPersonalProps] = defineField("usefulnessOfMaterial.notUsefulExplanationPersonal", validationOptions);

/**
 * SUITABILITY OF THE FORMATS USED DURING THE MATERIAL
 */
const [learnerEngagementTime, learnerEngagementTimeProps] = defineField("learnerEngagementTime", validationOptions);
const [innovativeElements, innovativeElementsProps] = defineField("innovativeElements", validationOptions);

/**
 * WAYS THE MATERIAL AFFECTS CLINICAL PRACTICE
 */
const [informationImplementation, informationImplementationProps] = defineField("informationImplementation", validationOptions);
const [futurePracticeInfluence, futurePracticeInfluenceProps] = defineField("futurePracticeInfluence", validationOptions);

/**
 * COMMERCIAL BIAS
 */
const [facultyConflictDeclaration, facultyConflictDeclarationProps] = defineField("facultyConflictDeclaration", validationOptions);
const [biasedPresentationExample, biasedPresentationExampleProps] = defineField("biasedPresentationExample", validationOptions);
const [informationFreeOfBias, informationFreeOfBiasProps] = defineField("informationFreeOfBias", validationOptions);

/**
 * GENERAL
 */
const [userAgreedToDataSharing, userAgreedToDataSharingProps] = defineField("userAgreedToDataSharing", validationOptions);
const [courseRating, courseRatingProps] = defineField("courseRating", validationOptions);

const onSubmit = handleSubmit(async values => {
  try {
    loading.value = true;

    if (!course.value?.id) {
      return;
    }

    const {
      country,
      usefulnessOfMaterial,
      overallImpressionProgramme,
      overallImpressionOrganization,

      achievementOfEducationalGoals,
      basedOnScientificEvidence,

      learnerEngagementTime,
      informationImplementation,

      facultyConflictDeclaration,
      informationFreeOfBias,

      ...restValues
    } = values;

    const body = {
      accreditationBody: 'eaccme',
      certificateType: 'cme_certificate',

      countryIsoCode: country.isoCode,
      countryStateName: country.stateName,

      usefulnessProfessional: UsefulnessOptionsValuesMapping[usefulnessOfMaterial.usefulnessProfessional],
      notUsefulExplanationProfessional: usefulnessOfMaterial.notUsefulExplanationProfessional,

      usefulnessPersonal: UsefulnessOptionsValuesMapping[usefulnessOfMaterial.usefulnessPersonal],
      notUsefulExplanationPersonal: usefulnessOfMaterial.notUsefulExplanationPersonal,

      overallImpressionProgramme: OverallImpressionOptionsValuesMapping[overallImpressionProgramme],
      overallImpressionOrganization: OverallImpressionOptionsValuesMapping[overallImpressionOrganization],

      achievementOfEducationalGoals: EducationalGoalsAndScientificEvidenceOptionsValuesMapping[achievementOfEducationalGoals],
      basedOnScientificEvidence: EducationalGoalsAndScientificEvidenceOptionsValuesMapping[basedOnScientificEvidence],

      learnerEngagementTime: LearnerEngagementTimeOptionsValuesMapping[learnerEngagementTime],
      informationImplementation: InformationImplementationOptionsValuesMapping[informationImplementation],

      facultyConflictDeclaration: FacultyConflictDeclarationOptionsValuesMapping[facultyConflictDeclaration],
      informationFreeOfBias: InformationFreeOfBiasOptionsValuesMapping[informationFreeOfBias],

      ...restValues,
    };

    const response = await submitClaimCertificateForm(course.value.id, body);

    startDownload(response.data.value as BlobPart, `CME Certificate - ${course.value?.courseTitle}.pdf`);
    dispatchMessage('Success!', 'success', "You have claimed the certificate!");
    if (course.value){
      course.value.certificateStatus = CourseCertificateStatus.ProClaimed;
    }
    emit('downloaded');

  } catch (error) {
    interface ClaimCertificateError {
      data: {
        message: string;
      };
    }

    dispatchMessage('Error!', 'error', (error as ClaimCertificateError)?.data?.message || 'Something went wrong. Please try again later.');

  } finally {
    loading.value = false;
  }
});

</script>

<template>
  <form
    class="flex flex-col gap-12"
    @submit="onSubmit"
  >
    <Card
      class="rounded-2xl p-0 text-neutral-90 shadow-medmastery"
      :pt="{
        body: {
          class: 'p-6'
        },
        content: {
          class: 'p-0'
        }
      }"
    >
      <template #content>
        <div class="flex flex-col gap-4">
          <!-- Logo and Title Section -->
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 xs:w-16 xs:h-16">
              <Logo class="w-full h-full" />
            </div>
            <div class="flex-1 text-15 text-neutral-90 font-bold uppercase">
              European Union of Medical Specialists (UEMS)
            </div>
          </div>

          <!-- Address Section -->
          <div class="flex flex-col gap-2">
            <div class="text-14 text-neutral-80 font-bold uppercase">
              European Accreditation Council on CME (EACCME®)
            </div>
            <div class="text-16 text-neutral-90 leading-130">
              <div>Rue de l'Industrie 24, B-1040, Brussels, Belgium</div>
              <div>T +32 2 649 51 64</div>
              <div class="flex items-center gap-1">
                <a
                  href="https://eaccme.uems.eu/"
                  target="_blank"
                  class="text-blue-70 text-14 font-normal leading-150 tracking-tight underline"
                >
                  eaccme.uems.eu
                </a>
                <span class="text-neutral-80 text-14">-</span>
                <a
                  href="mailto:accreditation@uems.eu"
                  class="text-blue-70 text-14 font-normal leading-150 tracking-tight underline"
                >
                  accreditation@uems.eu
                </a>
              </div>
            </div>
          </div>

          <!-- CME Credits Section -->
          <div class="flex flex-col justify-center items-stretch h-10 text-18 font-bold text-neutral-90 leading-normal">
            This certificate comes with {{course?.cmeCredits}} CME credits. <br>
          </div>

          <!-- Course Completion Section -->
          <div class="text-16 text-neutral-90 leading-150 font-normal">
            By submitting this form, you will complete the <strong>{{course?.courseTitle}}</strong>
            and claim the associated CME credits. The date on the certificate will be the date of submission of this form.
          </div>
        </div>
      </template>
    </Card>

    <!-- PROFESSIONAL INFORMATION -->
    <div class="flex flex-col gap-4">
      <Divider
        align="left"
        type="solid"
        class="m-0 mb-2"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">Personal Information</span>
      </Divider>

      <div class="grid grid-cols-2 gap-x-6">
        <div class="flex flex-col">
          <FormFieldLabel for="firstName">
            First name *
          </FormFieldLabel>

          <InputText
            id="firstName"
            v-model="firstName"
            :invalid="!!errors.firstName"
            :disabled="loading"
            aria-describedby="firstName-help"
            @blur="firstNameProps.onBlur"
          />

          <FormFieldError id="firstName-help">
            {{errors.firstName}}
          </FormFieldError>
        </div>

        <div class="flex flex-col">
          <FormFieldLabel for="lastName">
            Last name *
          </FormFieldLabel>

          <InputText
            id="lastName"
            v-model="lastName"
            :invalid="!!errors.lastName"
            :disabled="loading"
            aria-describedby="lastName-help"
            @blur="lastNameProps.onBlur"
          />

          <FormFieldError id="lastName-help">
            {{errors.lastName}}
          </FormFieldError>
        </div>
      </div>

      <div class="flex flex-col">
        <FormFieldLabel for="userIsDoctorOfMedicine">
          Are you a medical doctor (MD, DO, or equivalent) *
        </FormFieldLabel>

        <Dropdown
          id="userIsDoctorOfMedicine"
          v-model="userIsDoctorOfMedicine"
          aria-describedby="userIsDoctorOfMedicine-help"
          option-label="label"
          option-value="value"
          :invalid="!!errors.userIsDoctorOfMedicine"
          :disabled="loading"
          :options="options.userIsDoctorOfMedicine"
          @update:model-value="userIsDoctorOfMedicineProps.onBlur"
        />

        <FormFieldError id="userIsDoctorOfMedicine-help">
          {{errors.userIsDoctorOfMedicine}}
        </FormFieldError>
      </div>

      <div class="grid grid-cols-2 gap-x-6">
        <div class="flex flex-col">
          <FormFieldLabel for="countryIsoCode">
            In which country are you located? *
          </FormFieldLabel>

          <Dropdown
            id="countryIsoCode"
            v-model="countryIsoCode"
            option-label="name"
            option-value="code"
            editable
            aria-describedby="countryIsoCode-help"
            :invalid="!!errors['country.isoCode']"
            :disabled="loading"
            :options="countries"
            :show-clear="!!countryIsoCode"
            append-to="self"
            @update:model-value="countryIsoCodeProps.onBlur"
          >
            <template
              v-if="countryIsoCode"
              #value="{value}"
            >
              <div class="flex items-center">
                <MIcon
                  sprite-location="/icons/country_flags.svg"
                  :icon="value"
                  class="rounded-sm mr-2 country-flag-icon--value"
                />

                <div>{{countries.find((item) => item.code === value)?.name}}</div>
              </div>
            </template>

            <template #option="{option}">
              <div class="flex items-center">
                <MIcon
                  sprite-location="/icons/country_flags.svg"
                  :icon="option.code"
                  class="rounded-sm mr-2 country-flag-icon--option"
                />

                <div class="text-wrap">
                  {{option.name}}
                </div>
              </div>
            </template>
          </Dropdown>

          <FormFieldError id="countryIsoCode-help">
            {{errors['country.isoCode']}}
          </FormFieldError>
        </div>

        <div class="flex flex-col">
          <FormFieldLabel for="countryStateName">
            In which State do you live? (required for US citizens)
          </FormFieldLabel>

          <InputText
            id="countryStateName"
            v-model="countryStateName"
            :invalid="!!errors['country.stateName']"
            :disabled="loading"
            aria-describedby="countryStateName-help"
            @blur="countryStateNameProps.onBlur"
          />

          <FormFieldError id="countryStateName-help">
            {{errors['country.stateName']}}
          </FormFieldError>
        </div>
      </div>
    </div>

    <!-- QUALITY OF ELM -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">Quality of ELM</span>
      </Divider>

      <!-- Usefulness of material -->
      <div>
        <RatingScale
          v-model="usefulnessProfessional"
          title="How useful was the material for your clinical practice? *"
          :options="UsefulnessOptions.slice(1)"
          :invalid="!!errors['usefulnessOfMaterial.usefulnessProfessional']"
          @update:model-value="usefulnessProfessionalProps.onChange"
        />
        <FormFieldError id="usefulnessProfessional-help">
          {{errors['usefulnessOfMaterial.usefulnessProfessional']}}
        </FormFieldError>
      </div>

      <div
        v-if="usefulnessProfessional === UsefulnessOptions[1]"
        class="flex flex-col gap-2"
      >
        <FormFieldLabel for="notUsefulExplanationProfessional">
          If this material was not useful, please explain why *
        </FormFieldLabel>

        <Textarea
          id="notUsefulExplanationProfessional"
          v-model="notUsefulExplanationProfessional"
          placeholder="Enter your feedback here..."
          :disabled="loading"
          :invalid="!!errors['usefulnessOfMaterial.notUsefulExplanationProfessional']"
          aria-describedby="notUsefulExplanationProfessional-help"
          class="resize-y"
          rows="5"
          @blur="notUsefulExplanationProfessionalProps.onBlur"
        />

        <FormFieldError id="notUsefulExplanationProfessional-help">
          {{errors['usefulnessOfMaterial.notUsefulExplanationProfessional']}}
        </FormFieldError>
      </div>

      <div>
        <RatingScale
          v-model="overallImpressionProgramme"
          title="What was your overall impression of this material’s program? *"
          :options="OverallImpressionOptions"
          :invalid="!!errors.overallImpressionProgramme"
          @update:model-value="overallImpressionProgrammeProps.onChange"
        />
        <FormFieldError id="overallImpressionProgramme-help">
          {{errors.overallImpressionProgramme}}
        </FormFieldError>
      </div>

      <div>
        <RatingScale
          v-model="overallImpressionOrganization"
          title="What was your overall impression of this material’s organization? *"
          :options="OverallImpressionOptions"
          :invalid="!!errors.overallImpressionOrganization"
          @update:model-value="overallImpressionOrganizationProps.onChange"
        />
        <FormFieldError id="overallImpressionOrganization-help">
          {{errors.overallImpressionOrganization}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="bestAspect">
          Please describe the best aspect of this material *
        </FormFieldLabel>

        <Textarea
          id="bestAspect"
          v-model="bestAspect"
          placeholder="Enter your feedback here..."
          :disabled="loading"
          :invalid="!!errors.bestAspect"
          aria-describedby="bestAspect-help"
          class="resize-y"
          rows="5"
          @blur="bestAspectProps.onBlur"
        />

        <FormFieldError id="bestAspect-help">
          {{errors.bestAspect}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="worstAspect">
          Please describe the worst aspect of this material *
        </FormFieldLabel>

        <Textarea
          id="worstAspect"
          v-model="worstAspect"
          placeholder="Enter your feedback here..."
          :disabled="loading"
          :invalid="!!errors.worstAspect"
          aria-describedby="worstAspect-help"
          class="resize-y"
          rows="5"
          @blur="worstAspectProps.onBlur"
        />

        <FormFieldError id="worstAspect-help">
          {{errors.worstAspect}}
        </FormFieldError>
      </div>
    </div>

    <!-- RELEVANCE OF MATERIAL -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">
          Relevance of material
        </span>
      </Divider>

      <div>
        <RatingScale
          v-model="achievementOfEducationalGoals"
          title="Did the material fulfil your educational goals and expected learning outcomes? *"
          :options="EducationalGoalsAndScientificEvidenceOptions"
          :invalid="!!errors.achievementOfEducationalGoals"
          @update:model-value="achievementOfEducationalGoalsProps.onChange"
        />
        <FormFieldError id="achievementOfEducationalGoals-help">
          {{errors.achievementOfEducationalGoals}}
        </FormFieldError>
      </div>

      <div>
        <RatingScale
          v-model="basedOnScientificEvidence"
          title="Was the presented information well balanced and consistently supported by a valid scientific evidence base? *"
          :options="EducationalGoalsAndScientificEvidenceOptions"
          :invalid="!!errors.basedOnScientificEvidence"
          @update:model-value="basedOnScientificEvidenceProps.onChange"
        />
        <FormFieldError id="basedOnScientificEvidence-help">
          {{errors.basedOnScientificEvidence}}
        </FormFieldError>
      </div>

      <div>
        <RatingScale
          v-model="usefulnessPersonal"
          title="How useful to you personally was this module/course? *"
          :options="UsefulnessOptions"
          :invalid="!!errors['usefulnessOfMaterial.usefulnessPersonal']"
          @update:model-value="usefulnessPersonalProps.onChange"
        />
        <FormFieldError id="usefulnessPersonal-help">
          {{errors['usefulnessOfMaterial.usefulnessPersonal']}}
        </FormFieldError>
      </div>

      <div
        v-if="usefulnessPersonal === UsefulnessOptions[1]"
        class="flex flex-col gap-2"
      >
        <FormFieldLabel for="notUsefulExplanationPersonal">
          If this material was not useful, please explain why *
        </FormFieldLabel>

        <Textarea
          id="notUsefulExplanationPersonal"
          v-model="notUsefulExplanationPersonal"
          placeholder="Enter your feedback here..."
          :disabled="loading"
          :invalid="!!errors['usefulnessOfMaterial.notUsefulExplanationPersonal']"
          aria-describedby="notUsefulExplanationPersonal-help"
          class="resize-y"
          rows="5"
          @blur="notUsefulExplanationPersonalProps.onBlur"
        />

        <FormFieldError id="notUsefulExplanationPersonal-help">
          {{errors['usefulnessOfMaterial.notUsefulExplanationPersonal']}}
        </FormFieldError>
      </div>
    </div>

    <!-- SUITABILITY OF THE FORMATS USED DURING THE MATERIAL -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">
          Suitability of the formats used during the material
        </span>
      </Divider>

      <div>
        <RatingScale
          v-model="learnerEngagementTime"
          title="Was there adequate time available for learner engagement? *"
          :options="LearnerEngagementTimeOptions"
          :invalid="!!errors.learnerEngagementTime"
          @update:model-value="learnerEngagementTimeProps.onChange"
        />
        <FormFieldError id="learnerEngagementTime-help">
          {{errors.learnerEngagementTime}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="innovativeElements">
          Can you indicate any innovative elements during the material? *
        </FormFieldLabel>

        <Textarea
          id="innovativeElements"
          v-model="innovativeElements"
          placeholder="Enter your feedback here..."
          :disabled="loading"
          :invalid="!!errors.innovativeElements"
          aria-describedby="innovativeElements-help"
          class="resize-y"
          rows="5"
          @blur="innovativeElementsProps.onBlur"
        />

        <FormFieldError id="innovativeElements-help">
          {{errors.innovativeElements}}
        </FormFieldError>
      </div>
    </div>

    <!-- WAYS THE MATERIAL AFFECTS CLINICAL PRACTICE -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">
          Ways the material affects clinical practice
        </span>
      </Divider>

      <div>
        <RatingScale
          v-model="informationImplementation"
          title="Will the information you learned be implemented in your practice? *"
          :options="InformationImplementationOptions"
          :invalid="!!errors.informationImplementation"
          @update:model-value="informationImplementationProps.onChange"
        />
        <FormFieldError id="informationImplementation-help">
          {{errors.informationImplementation}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="futurePracticeInfluence">
          Can you provide ONE example how this material will influence your future practice? *
        </FormFieldLabel>

        <Textarea
          id="futurePracticeInfluence"
          v-model="futurePracticeInfluence"
          placeholder="Enter your feedback here..."
          :disabled="loading"
          :invalid="!!errors.futurePracticeInfluence"
          aria-describedby="futurePracticeInfluence-help"
          class="resize-y"
          rows="5"
          @blur="futurePracticeInfluenceProps.onBlur"
        />

        <FormFieldError id="futurePracticeInfluence-help">
          {{errors.futurePracticeInfluence}}
        </FormFieldError>
      </div>
    </div>

    <!-- COMMERCIAL BIAS -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">
          Commercial bias
        </span>
      </Divider>

      <div>
        <RatingScale
          v-model="facultyConflictDeclaration"
          title="Did all the faculty members provide their potential conflict of interest declarations? *"
          :options="FacultyConflictDeclarationOptions"
          :invalid="!!errors.facultyConflictDeclaration"
          @update:model-value="facultyConflictDeclarationProps.onChange"
        />
        <FormFieldError id="facultyConflictDeclaration-help">
          {{errors.facultyConflictDeclaration}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="biasedPresentationExample">
          Can you provide an example of biased presentation in this activity? *
        </FormFieldLabel>

        <Textarea
          id="biasedPresentationExample"
          v-model="biasedPresentationExample"
          placeholder="Enter your feedback here..."
          :disabled="loading"
          :invalid="!!errors.biasedPresentationExample"
          aria-describedby="biasedPresentationExample-help"
          class="resize-y"
          rows="5"
          @blur="biasedPresentationExampleProps.onBlur"
        />

        <FormFieldError id="biasedPresentationExample-help">
          {{errors.biasedPresentationExample}}
        </FormFieldError>
      </div>

      <div>
        <RatingScale
          v-model="informationFreeOfBias"
          title="Do you agree that the information was overall free of commercial and other bias? *"
          :options="InformationFreeOfBiasOptions"
          :invalid="!!errors.informationFreeOfBias"
          @update:model-value="informationFreeOfBiasProps.onChange"
        />
        <FormFieldError id="informationFreeOfBias-help">
          {{errors.informationFreeOfBias}}
        </FormFieldError>
      </div>
    </div>
    <Divider class="m-0" />

    <!-- AGREEMENT -->
    <div class="flex flex-col gap-3">
      <div>
        <label class="flex gap-2 text-14 font-bold">
          <Checkbox
            v-model="userAgreedToDataSharing"
            binary
            :disabled="loading"
            @update:model-value="userAgreedToDataSharingProps.onBlur"
          />
          <span>
            I agree that the data submitted with this form, the date of submission,
            the name of the completed activity and the quiz results are transmitted
            to our CME accrediting organization. *
          </span>
        </label>
        <FormFieldError id="userAgreedToDataSharing-help">
          {{errors.userAgreedToDataSharing}}
        </FormFieldError>
      </div>

      <div class="text-neutral-70 text-14 leading-150">
        UEMS <sub>aisbl</sub> – Union Européenne des Médecins Spécialistes <br>
        VAT n° BE 0469.067.848 RPM Bruxelles-Brussels <br>
        EU Transparency Register ID 219038730914-92
      </div>
    </div>

    <Divider class="m-0" />

    <!-- COURSE RATING -->
    <div class="flex flex-col gap-4">
      <div class="flex flex-col items-center gap-2">
        <div class="text-16">
          Please rate the learning experience of this course.
        </div>
        <StarRating
          v-model="courseRating"
          :disabled="loading"
          @update:model-value="courseRatingProps.onBlur"
        />
        <FormFieldError id="courseRating-help">
          {{errors.courseRating}}
        </FormFieldError>
      </div>

      <div class="flex justify-center">
        <Button
          :loading="loading"
          class="font-bold !text-22 flex gap-2 px-12 bg-blue-30 text-neutral-90 border-none rounded-full"
          type="submit"
          @click="onSubmit"
        >
          <FontAwesomeIcon
            v-if="loading"
            icon="fa-circle-notch"
            class="animate-spin"
          />
          <FontAwesomeIcon
            v-else
            icon="fa-file-certificate"
          />
          <span>Claim certificate</span>
        </Button>
      </div>
      <div class="flex justify-center mb-12">
        <FormFieldError
          v-if="Object.keys(errors).length > 0"
          id="form-error"
        >
          Validation failed please check the form for errors
        </FormFieldError>
      </div>
    </div>
  </form>
</template>